import { Icon } from '@iconify/react';
import { Button, Card, CardBody, CardHeader, Tooltip } from '@nextui-org/react';
import React from 'react';

interface ListCardProps {
  title: string;
  subtitle?: string;
  description?: string;
  onEdit?: () => void;
  onDelete?: () => void;
  onStart?: () => void;
  isDeleting?: boolean;
  isStarting?: boolean;
  viewType?: 'tile' | 'list';
  actions?: React.ReactNode;
}

const ListCard: React.FC<ListCardProps> = ({
  title,
  subtitle,
  description,
  onEdit,
  onDelete,
  onStart,
  isDeleting = false,
  isStarting = false,
  viewType = 'tile',
  actions = null,
}) => {
  const handleContentClick = () => {
    onEdit?.();
  };

  const handleContentKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (!onEdit) return;
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onEdit();
    }
  };

  const displayActionItems = () => {
    return (
      <div className="flex gap-2">
        {onStart && (
          <Tooltip content="Start Practice">
            <Button
              isIconOnly
              variant="light"
              size="sm"
              onClick={onStart}
              aria-label="Start Practice"
            >
              {isStarting ? (
                <Icon
                  icon="lucide:loader-circle"
                  width="20"
                  className="animate-spin"
                />
              ) : (
                <Icon icon="proicons:play" width="20" />
              )}
            </Button>
          </Tooltip>
        )}
        {onEdit && (
          <Tooltip content="Edit">
            <Button
              isIconOnly
              variant="light"
              size="sm"
              onClick={onEdit}
              aria-label="Edit"
            >
              <Icon icon="akar-icons:edit" width="20" />
            </Button>
          </Tooltip>
        )}
        {onDelete && (
          <Tooltip content="Delete">
            <Button
              isIconOnly
              variant="light"
              size="sm"
              // color="danger"
              onClick={onDelete}
              aria-label="Delete"
            >
              {isDeleting ? (
                <Icon
                  icon="lucide:loader-circle"
                  width="20"
                  className="animate-spin"
                />
              ) : (
                <Icon icon="proicons:delete" width="20" />
              )}
            </Button>
          </Tooltip>
        )}
        {actions}
      </div>
    );
  };
  return (
    <Card
      className={`w-full bg-content1 dark:bg-default-100 p-2 border-1 ${
        viewType === 'list' ? 'flex-row items-center' : ''
      }`}
      shadow="none"
    >
      <CardHeader
        className={`flex justify-between items-center py-2 px-4 ${
          viewType === 'list' ? 'flex-1 min-w-0' : ''
        }`}
      >
        <div
          className={`min-w-0 flex-1 ${
            subtitle ? 'flex justify-between items-center w-full' : ''
          } ${onEdit ? 'cursor-pointer' : ''}`}
          onClick={onEdit ? handleContentClick : undefined}
          onKeyDown={onEdit ? handleContentKeyDown : undefined}
          role={onEdit ? 'button' : undefined}
          tabIndex={onEdit ? 0 : undefined}
        >
          <h4
            className={` mr-2 max-w-[100%] ${
              viewType === 'list' ? 'text-base ' : 'text-medium text-start'
            }`}
          >
            {title}
          </h4>
        </div>
        <div className="flex shrink-0">{displayActionItems()}</div>
      </CardHeader>

      {(description || subtitle) && viewType === 'tile' && (
        <>
          <CardBody
            className={`p-4 py-2 ${onEdit ? 'cursor-pointer' : ''}`}
            onClick={onEdit ? handleContentClick : undefined}
            onKeyDown={onEdit ? handleContentKeyDown : undefined}
            role={onEdit ? 'button' : undefined}
            tabIndex={onEdit ? 0 : undefined}
          >
            {description && (
              <p className="text-small text-default-500 line-clamp-2 mb-4">
                {description}
              </p>
            )}
            {subtitle && (
              <p
                className="text-[0.75rem] text-default-400 "
                style={{ fontStyle: 'italic' }}
              >
                {subtitle}
              </p>
            )}
          </CardBody>
        </>
      )}
    </Card>
  );
};

export default ListCard;
